import { safeParseFloat } from '@/lib/utils'

/**
 * Format number string with commas for display
 * Example: "1000" -> "1,000", "1000.5" -> "1,000.5"
 */
export const formatNumberWithCommas = (value: string | number, precision?: number): string => {
  const strValue = typeof value === 'number' ? value.toString() : value
  const cleaned = strValue.replace(/[^\d.]/g, '')

  if (!cleaned || cleaned === '.') {
    return cleaned
  }

  const parts = cleaned.split('.')
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('')
  }

  const [integer, decimal] = parts
  const num = safeParseFloat(integer)
  if (isNaN(num)) {
    return cleaned
  }

  const formattedInteger = num.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })

  if (decimal !== undefined) {
    if (precision !== undefined) {
      return `${formattedInteger}.${decimal.slice(0, precision)}`
    }
    return `${formattedInteger}.${decimal}`
  }
  return formattedInteger
}

/**
 * Cleanly format a numeric value (string or number) to a fixed precision with commas.
 * Useful for price initialization.
 */
export const formatNumberWithPrecision = (
  value: string | number,
  precision: number = 2
): string => {
  // Not safeParseFloat: it maps garbage to 0, which would prefill a '0.00' price.
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value
  if (!Number.isFinite(num)) return ''

  // formatNumberWithCommas strips '-' (it doubles as the size-input sanitizer), so carry the
  // sign ourselves: spread/combo prices can be negative. A value that rounds to zero is '0'.
  const fixed = Math.abs(num).toFixed(precision)
  const negative = num < 0 && Number(fixed) !== 0
  return `${negative ? '-' : ''}${formatNumberWithCommas(fixed)}`
}

export const getPrecisionFromStep = (
  step: string | number | null | undefined,
  fallback: number = 2
): number => {
  if (step == null || step === '') return fallback

  const normalized = step.toString().trim().toLowerCase()
  if (!normalized) return fallback

  if (normalized.includes('e-')) {
    // '1.5e-7' is 0.00000015: the exponent plus the mantissa's own decimals.
    const [mantissa, exponent] = normalized.split('e-')
    const mantissaDecimals = mantissa.includes('.') ? mantissa.split('.')[1].length : 0
    return Number(exponent) + mantissaDecimals || fallback
  }

  return normalized.includes('.') ? normalized.split('.')[1].length : 0
}

/**
 * Remove commas from formatted number string
 * Example: "1,000.5" -> "1000.5"
 */
export const removeCommas = (value: string): string => {
  return value.replace(/,/g, '')
}

/**
 * Check if input is a valid number format (digits, commas, and one decimal point)
 * Example: "1,000.5" -> true, "1,00.0.5" -> false
 */
export const isValidNumberInput = (value: string): boolean => {
  return /^[\d,]*\.?\d*$/.test(value)
}

/**
 * Parse formatted number string to number
 * Example: "1,000.5" -> 1000.5
 */
export const parseFormattedNumber = (value: string): number => {
  return safeParseFloat(value)
}

/**
 * Format number with compact notation (K, M, B)
 * Example: 1500 -> "1.5K", 1500000 -> "1.5M"
 */
export const formatCompactNumber = (value: string | number, precision: number = 2): string => {
  const num = typeof value === 'string' ? safeParseFloat(value) : value
  if (isNaN(num) || num === 0) return '0'

  const absNum = Math.abs(num)
  const sign = num < 0 ? '-' : ''

  if (absNum >= 1_000_000_000) {
    return `${sign}${(absNum / 1_000_000_000).toFixed(precision)}B`
  } else if (absNum >= 1_000_000) {
    return `${sign}${(absNum / 1_000_000).toFixed(precision)}M`
  } else if (absNum >= 1_000) {
    return `${sign}${(absNum / 1_000).toFixed(precision)}K`
  } else {
    return `${sign}${absNum.toFixed(precision)}`
  }
}
